import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/api';
import { User } from '../types';

interface UserContextType {
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  hasPermission: (permission: string) => boolean;
  /** null = all cameras allowed; number[] = only those IDs */
  allowedCameraIds: number[] | null;
  hasCameraAccess: (cameraId: number) => boolean;
  hasCameraEventAccess: (cameraId: number) => boolean;
  isAdmin: boolean;
  isOperator: boolean;
  isViewer: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const savedUser = localStorage.getItem('astravision_user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      console.error('Error parsing saved user:', error);
      localStorage.removeItem('astravision_user');
      return null;
    }
  });
  const [allowedCameraIds, setAllowedCameraIds] = useState<number[] | null>(null);

  // Listen for storage changes (like when cache is cleared)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'astravision_user' && !e.newValue) {
        setUser(null);
        setAllowedCameraIds(null);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Auto-load role permissions and camera_ids whenever a user is set without permissions.
  // Admins bypass all checks so they don't need a permission list.
  useEffect(() => {
    if (!user || user.permissions !== undefined) {
      if (user?.role === 'admin') setAllowedCameraIds(null);
      return;
    }
    authService.getCurrentUser()
      .then(me => {
        const perms: string[] = (me as any).permissions ?? [];
        const camIds: number[] | null = (me as any).camera_ids ?? null;
        const updated = { ...user, permissions: perms };
        setUser(updated);
        localStorage.setItem('astravision_user', JSON.stringify(updated));
        setAllowedCameraIds(camIds);
      })
      .catch(() => {});
  }, [user]);

  const login = (userData: User) => {
    setUser(userData);
    localStorage.setItem('astravision_user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setAllowedCameraIds(null);
    localStorage.removeItem('astravision_user');
    localStorage.removeItem('astravision_camera_cache');
  };

  const updateUser = (updates: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem('astravision_user', JSON.stringify(updated));
      return updated;
    });
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions?.includes(permission) || false;
  };

  const hasCameraAccess = (cameraId: number): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (allowedCameraIds === null) return true;
    return allowedCameraIds.includes(cameraId);
  };

  const hasCameraEventAccess = (cameraId: number): boolean => {
    if (!user) return false;
    if (user.role === 'admin' || user.permissions?.includes('*') || user.permissions?.includes('camera_events:all')) {
      return true;
    }
    const hasSpecificEventPerms = user.permissions?.some(p => p.startsWith('camera_event:'));
    if (hasSpecificEventPerms) {
      return user.permissions?.includes(`camera_event:${cameraId}`) || false;
    }
    return hasCameraAccess(cameraId);
  };

  const isAdmin = user?.role === 'admin';
  const isOperator = user?.role === 'operator' || isAdmin;
  const isViewer = user?.role === 'viewer' || isOperator;

  return (
    <UserContext.Provider
      value={{
        user,
        login,
        logout,
        updateUser,
        hasPermission,
        allowedCameraIds,
        hasCameraAccess,
        hasCameraEventAccess,
        isAdmin,
        isOperator,
        isViewer,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
